import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { DialogService } from 'app-shared-ui';
import {
  AdvancedSpecializedImaging,
  CTScan,
  DigitalXRay,
  EmergencyRooms,
  FunctionalPreventiveImaging,
  HospitalFacility,
  HospitalSpecialities,
  IBodyScanServices,
  IHospitalServices,
  InpatientFacilities,
  InterventionalRadiology,
  MajorCategories,
  MRI,
  NuclearMedicine,
  OperativeFacilities,
  OutpatientServices,
  PETScan,
  PhysiotherapyServices,
  RadiologyFacilities,
  TimeUnit,
  Ultrasound,
  XRay,
} from '@alpha/types';
import {
  BodyScanAppointmentService,
  ClinicAppointmentService,
} from 'app-service';
import { Icon } from 'app-core';

interface IFilterItem {
  title: string;
  filters: {
    list: () => Array<{
      item: string;
      active: boolean;
      cost?: string;
      timespan?: string;
    }>;
  };
  expanded?: boolean;
}

import IconType = Data.Core.IconType;
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-clinic-services',
  templateUrl: './clinic-services.component.html',
  styleUrls: ['./clinic-services.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [BodyScanAppointmentService, DialogService],
})
export class ClinicServicesComponent implements OnInit, OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  private doctorId: string = '';
  private clinicId: string = '';
  private emergencyRooms: Array<number> = [];
  private hospitalFacilities: Array<number> = [];
  private hospitalSpecialities: Array<number> = [];
  private inpatientFacilities: Array<number> = [];
  private operativeFacilities: Array<number> = [];
  private outpatientServices: Array<number> = [];
  private physiotherapyServices: Array<number> = [];
  private radiologyFacilities: Array<number> = [];
  private advancedSpecializedImagingServices: Array<number> = [];
  private cTScanServices: Array<number> = [];
  private digitalXRayServices: Array<number> = [];
  private functionalPreventiveImagingServices: Array<FunctionalPreventiveImaging> =
    [];
  public rupeeIcon: Icon = new Icon({
    icon: IconType.rupeesIcon,
    hover: false,
    badge: {
      size: 'small',
      color: 'accent',
      content: '',
    },
  });
  public timerIcon: Icon = new Icon({
    icon: IconType.timerIcon,
    hover: false,
    badge: {
      size: 'small',
      color: 'accent',
      content: '',
    },
  });
  private interventionalRadiologyServices: Array<InterventionalRadiology> = [];
  private mRIServices: Array<MRI> = [];
  private nuclearMedicineServices: Array<NuclearMedicine> = [];
  private pETScanServices: Array<PETScan> = [];
  private ultrasoundServices: Array<Ultrasound> = [];
  private xRayServices: Array<XRay> = [];
  private categoryId: MajorCategories = MajorCategories.Doctor;
  public items: Array<IFilterItem> = [];
  public loading: boolean = false;
  public TimeUnit!: TimeUnit;

  public constructor(
    private readonly _bodyScanAppointmentService: BodyScanAppointmentService,
    private readonly _appointmentService: ClinicAppointmentService,
    private readonly _cdRef: ChangeDetectorRef,
    private readonly _route: Router,
    private readonly _router: ActivatedRoute,
  ) {}

  public ngOnInit(): void {
    const route = this._route.url.split('/');
    this.doctorId = route[2] === 'view' ? route[4] : route[3];
    this.clinicId =
      route[2] === 'view' ? route[5].split('?')[0] : route[4].split('?')[0];

    this._router.queryParamMap
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((paramMap: ParamMap) => {
        this.processRouteParameters(paramMap);

        const url = new URL(this._route.url, environment.baseURL);
        this.categoryId = Number(url.searchParams.get('c'));
        const isServicesInViewAppointment = url.pathname.includes(
          '/appointment/view/services',
        );
        const isServicesInBookAppointment = url.pathname.includes(
          '/appointment/services',
        );

        if (isServicesInViewAppointment) {
          if (
            Number(this._router.snapshot.queryParamMap.get('acf')) ===
            Number(MajorCategories.Doctor)
          ) {
            this.getClinicServices();
          }
          if (
            Number(this._router.snapshot.queryParamMap.get('acf')) ===
            Number(MajorCategories.BodyScan)
          ) {
            this.getBodyScanClinicServices();
          }
        }

        if (isServicesInBookAppointment) {
          if (
            Number(this._router.snapshot.queryParamMap.get('c')) ===
            Number(MajorCategories.Doctor)
          ) {
            this.getClinicServices();
          }
          if (
            Number(this._router.snapshot.queryParamMap.get('c')) ===
            Number(MajorCategories.BodyScan)
          ) {
            this.getBodyScanClinicServices();
          }
        }
      });
  }

  private processRouteParameters(paramMap: any): void {
    // Clinic Params
    if (paramMap.params.er) {
      this.emergencyRooms =
        typeof paramMap.params.er === 'string'
          ? [Number(paramMap.params.er)]
          : paramMap.params.er.map((item: string) => Number(item));
    }

    if (paramMap.params.hf) {
      this.hospitalFacilities =
        typeof paramMap.params.hf === 'string'
          ? [Number(paramMap.params.hf)]
          : paramMap.params.hf.map((item: string) => Number(item));
    }

    if (paramMap.params.hs) {
      this.hospitalSpecialities =
        typeof paramMap.params.hs === 'string'
          ? [Number(paramMap.params.hs)]
          : paramMap.params.hs.map((item: string) => Number(item));
    }

    if (paramMap.params.if) {
      this.inpatientFacilities =
        typeof paramMap.params.if === 'string'
          ? [Number(paramMap.params.if)]
          : paramMap.params.if.map((item: string) => Number(item));
    }

    if (paramMap.params.of) {
      this.operativeFacilities =
        typeof paramMap.params.of === 'string'
          ? [Number(paramMap.params.of)]
          : paramMap.params.of.map((item: string) => Number(item));
    }

    if (paramMap.params.os) {
      this.outpatientServices =
        typeof paramMap.params.os === 'string'
          ? [Number(paramMap.params.os)]
          : paramMap.params.os.map((item: string) => Number(item));
    }

    if (paramMap.params.ps) {
      this.physiotherapyServices =
        typeof paramMap.params.ps === 'string'
          ? [Number(paramMap.params.ps)]
          : paramMap.params.ps.map((item: string) => Number(item));
    }

    if (paramMap.params.rf) {
      this.radiologyFacilities =
        typeof paramMap.params.rf === 'string'
          ? [Number(paramMap.params.rf)]
          : paramMap.params.rf.map((item: string) => Number(item));
    }

    // Body Scan Clinic Params
    if (paramMap.params.ai) {
      this.advancedSpecializedImagingServices =
        typeof paramMap.params.ai === 'string'
          ? [Number(paramMap.params.ai)]
          : paramMap.params.ai.map((item: string) => Number(item));
    }

    if (paramMap.params.cs) {
      this.cTScanServices =
        typeof paramMap.params.cs === 'string'
          ? [Number(paramMap.params.cs)]
          : paramMap.params.cs.map((item: string) => Number(item));
    }

    if (paramMap.params.dx) {
      this.digitalXRayServices =
        typeof paramMap.params.dx === 'string'
          ? [Number(paramMap.params.dx)]
          : paramMap.params.dx.map((item: string) => Number(item));
    }

    if (paramMap.params.fi) {
      this.functionalPreventiveImagingServices =
        typeof paramMap.params.fi === 'string'
          ? [Number(paramMap.params.fi)]
          : paramMap.params.fi.map((item: string) => Number(item));
    }

    if (paramMap.params.ir) {
      this.interventionalRadiologyServices =
        typeof paramMap.params.ir === 'string'
          ? [Number(paramMap.params.ir)]
          : paramMap.params.ir.map((item: string) => Number(item));
    }

    if (paramMap.params.mi) {
      this.mRIServices =
        typeof paramMap.params.mi === 'string'
          ? [Number(paramMap.params.mi)]
          : paramMap.params.mi.map((item: string) => Number(item));
    }

    if (paramMap.params.nm) {
      this.nuclearMedicineServices =
        typeof paramMap.params.nm === 'string'
          ? [Number(paramMap.params.nm)]
          : paramMap.params.nm.map((item: string) => Number(item));
    }

    if (paramMap.params.psn) {
      this.pETScanServices =
        typeof paramMap.params.psn === 'string'
          ? [Number(paramMap.params.psn)]
          : paramMap.params.psn.map((item: string) => Number(item));
    }

    if (paramMap.params.ud) {
      this.ultrasoundServices =
        typeof paramMap.params.ud === 'string'
          ? [Number(paramMap.params.ud)]
          : paramMap.params.ud.map((item: string) => Number(item));
    }

    if (paramMap.params.xy) {
      this.xRayServices =
        typeof paramMap.params.xy === 'string'
          ? [Number(paramMap.params.xy)]
          : paramMap.params.xy.map((item: string) => Number(item));
    }
  }

  private getClinicServices(): void {
    this._appointmentService
      .getClinicServices({
        doctorId: {
          _id: this.doctorId,
        },
        clinicId: {
          _id: this.clinicId,
        },
      })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((service: Partial<IHospitalServices>) => {
        if (service) {
          if (service!.hospitalServices) {
            let listItems: Array<{ item: string; active: boolean }> = [];

            service!.hospitalServices.forEach((item) => {
              let itemx: { item: string; active: boolean };

              itemx = {
                item: HospitalFacility[item],
                active: this.hospitalFacilities.some((hf) => hf === item)
                  ? true
                  : false,
              };

              listItems.push(itemx);
            });
            this.items.push({
              title: 'Hospital Services',
              filters: {
                list: () => listItems,
              },
              expanded: true,
            });
          }
          if (service.outpatientServices) {
            let listItems: Array<{ item: string; active: boolean }> = [];

            service.outpatientServices.forEach((item) => {
              let itemx: { item: string; active: boolean };

              itemx = {
                item: OutpatientServices[item],
                active: this.outpatientServices.some((hf) => hf === item)
                  ? true
                  : false,
              };

              listItems.push(itemx);
            });
            this.items.push({
              title: 'Outpatient Services',
              filters: {
                list: () => listItems,
              },
              expanded: true,
            });
          }
          if (service.inpatientFacilities) {
            let listItems: Array<{ item: string; active: boolean }> = [];

            service.inpatientFacilities.forEach((item) => {
              let itemx: { item: string; active: boolean };

              itemx = {
                item: InpatientFacilities[item],
                active: this.inpatientFacilities.some((hf) => hf === item)
                  ? true
                  : false,
              };

              listItems.push(itemx);
            });
            this.items.push({
              title: 'Inpatient Facilities',
              filters: {
                list: () => listItems,
              },
              expanded: true,
            });
          }
          if (service.operativeFacilities) {
            let listItems: Array<{ item: string; active: boolean }> = [];

            service.operativeFacilities.forEach((item) => {
              let itemx: { item: string; active: boolean };

              itemx = {
                item: OperativeFacilities[item],
                active: this.operativeFacilities.some((hf) => hf === item)
                  ? true
                  : false,
              };

              listItems.push(itemx);
            });
            this.items.push({
              title: 'Operative Facilities',
              filters: {
                list: () => listItems,
              },
              expanded: true,
            });
          }
          if (service.emergencyRooms) {
            let listItems: Array<{ item: string; active: boolean }> = [];

            service.emergencyRooms.forEach((item) => {
              let itemx: { item: string; active: boolean };

              itemx = {
                item: EmergencyRooms[item],
                active: this.emergencyRooms.some((hf) => hf === item)
                  ? true
                  : false,
              };

              listItems.push(itemx);
            });
            this.items.push({
              title: 'Emergency Rooms',
              filters: {
                list: () => listItems,
              },
              expanded: true,
            });
          }
          if (service.radiologyFacilities) {
            let listItems: Array<{ item: string; active: boolean }> = [];

            service.radiologyFacilities.forEach((item) => {
              let itemx: { item: string; active: boolean };

              itemx = {
                item: RadiologyFacilities[item],
                active: this.radiologyFacilities.some((hf) => hf === item)
                  ? true
                  : false,
              };

              listItems.push(itemx);
            });
            this.items.push({
              title: 'Radiology Facilities',
              filters: {
                list: () => listItems,
              },
              expanded: true,
            });
          }
          if (service.physiotherapyServices) {
            let listItems: Array<{ item: string; active: boolean }> = [];

            service.physiotherapyServices.forEach((item) => {
              let itemx: { item: string; active: boolean };

              itemx = {
                item: PhysiotherapyServices[item],
                active: this.physiotherapyServices.some((hf) => hf === item)
                  ? true
                  : false,
              };

              listItems.push(itemx);
            });
            this.items.push({
              title: 'Physiotherapy Services',
              filters: {
                list: () => listItems,
              },
              expanded: true,
            });
          }
          if (service.hospitalSpecialities) {
            let listItems: Array<{ item: string; active: boolean }> = [];

            service.hospitalSpecialities.forEach((item) => {
              let itemx: { item: string; active: boolean };

              itemx = {
                item: HospitalSpecialities[item],
                active: this.hospitalSpecialities.some((hf) => hf === item)
                  ? true
                  : false,
              };

              listItems.push(itemx);
            });
            this.items.push({
              title: 'Hospital Specialities',
              filters: {
                list: () => listItems,
              },
              expanded: true,
            });
          }
          this._cdRef.markForCheck();
        }
      });
  }

  private getBodyScanClinicServices(): void {
    this._bodyScanAppointmentService
      .getClinicServices({
        doctorId: {
          _id: this.doctorId,
        },
        clinicId: {
          _id: this.clinicId,
        },
      })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((service: Partial<IBodyScanServices>) => {
        if (service) {
          if (service!.advancedSpecializedImagingServices) {
            let listItems: Array<{ item: string; active: boolean }> = [];

            service!.advancedSpecializedImagingServices.forEach((item) => {
              let itemx: {
                item: string;
                active: boolean;
                cost?: string;
                timespan?: string;
              };

              itemx = {
                item: AdvancedSpecializedImaging[item.service].replace(
                  /_/g,
                  ' ',
                ),
                active: this.advancedSpecializedImagingServices.some(
                  (hf) => hf === item.service,
                )
                  ? true
                  : false,
                cost: item.cost.toFixed(2),
                timespan: `${item.timespan}  ${TimeUnit[item.timeunit]}`,
              };

              listItems.push(itemx);
            });
            this.items.push({
              title: 'Advanced Specialized Imaging',
              filters: {
                list: () => listItems,
              },
              expanded: true,
            });
          }
          if (service.cTScanServices) {
            let listItems: Array<{ item: string; active: boolean }> = [];

            service.cTScanServices.forEach((item) => {
              let itemx: {
                item: string;
                active: boolean;
                cost?: string;
                timespan?: string;
              };

              itemx = {
                item: CTScan[item.service].replace(/_/g, ' '),
                active: this.cTScanServices.some((hf) => hf === item.service)
                  ? true
                  : false,
                cost: item.cost.toFixed(2),
                timespan: `${item.timespan}  ${TimeUnit[item.timeunit]}`,
              };

              listItems.push(itemx);
            });
            this.items.push({
              title: 'CT Scan',
              filters: {
                list: () => listItems,
              },
              expanded: true,
            });
          }
          if (service.digitalXRayServices) {
            let listItems: Array<{ item: string; active: boolean }> = [];

            service.digitalXRayServices.forEach((item) => {
              let itemx: {
                item: string;
                active: boolean;
                cost?: string;
                timespan?: string;
              };

              itemx = {
                item: DigitalXRay[item.service].replace(/_/g, ' '),
                active: this.digitalXRayServices.some(
                  (hf) => hf === item.service,
                )
                  ? true
                  : false,
                cost: item.cost.toFixed(2),
                timespan: `${item.timespan}  ${TimeUnit[item.timeunit]}`,
              };

              listItems.push(itemx);
            });
            this.items.push({
              title: 'Digital XRay',
              filters: {
                list: () => listItems,
              },
              expanded: true,
            });
          }
          if (service.functionalPreventiveImagingServices) {
            let listItems: Array<{ item: string; active: boolean }> = [];

            service.functionalPreventiveImagingServices.forEach((item) => {
              let itemx: {
                item: string;
                active: boolean;
                cost?: string;
                timespan?: string;
              };

              itemx = {
                item: FunctionalPreventiveImaging[item.service].replace(
                  /_/g,
                  ' ',
                ),
                active: this.functionalPreventiveImagingServices.some(
                  (hf) => hf === item.service,
                )
                  ? true
                  : false,
                cost: item.cost.toFixed(2),
                timespan: `${item.timespan}  ${TimeUnit[item.timeunit]}`,
              };

              listItems.push(itemx);
            });
            this.items.push({
              title: 'Functional Preventive Imaging',
              filters: {
                list: () => listItems,
              },
              expanded: true,
            });
          }
          if (service.interventionalRadiologyServices) {
            let listItems: Array<{ item: string; active: boolean }> = [];

            service.interventionalRadiologyServices.forEach((item) => {
              let itemx: {
                item: string;
                active: boolean;
                cost?: string;
                timespan?: string;
              };

              itemx = {
                item: InterventionalRadiology[item.service].replace(/_/g, ' '),
                active: this.interventionalRadiologyServices.some(
                  (hf) => hf === item.service,
                )
                  ? true
                  : false,
                cost: item.cost.toFixed(2),
                timespan: `${item.timespan}  ${TimeUnit[item.timeunit]}`,
              };

              listItems.push(itemx);
            });
            this.items.push({
              title: 'Interventional Radiology',
              filters: {
                list: () => listItems,
              },
              expanded: true,
            });
          }
          if (service.mRIServices) {
            let listItems: Array<{ item: string; active: boolean }> = [];

            service.mRIServices.forEach((item) => {
              let itemx: {
                item: string;
                active: boolean;
                cost?: string;
                timespan?: string;
              };

              itemx = {
                item: MRI[item.service].replace(/_/g, ' '),
                active: this.mRIServices.some((hf) => hf === item.service)
                  ? true
                  : false,
                cost: item.cost.toFixed(2),
                timespan: `${item.timespan}  ${TimeUnit[item.timeunit]}`,
              };

              listItems.push(itemx);
            });
            this.items.push({
              title: 'MRI',
              filters: {
                list: () => listItems,
              },
              expanded: true,
            });
          }
          if (service.nuclearMedicineServices) {
            let listItems: Array<{ item: string; active: boolean }> = [];

            service.nuclearMedicineServices.forEach((item) => {
              let itemx: {
                item: string;
                active: boolean;
                cost?: string;
                timespan?: string;
              };

              itemx = {
                item: NuclearMedicine[item.service].replace(/_/g, ' '),
                active: this.nuclearMedicineServices.some(
                  (hf) => hf === item.service,
                )
                  ? true
                  : false,
                cost: item.cost.toFixed(2),
                timespan: `${item.timespan}  ${TimeUnit[item.timeunit]}`,
              };

              listItems.push(itemx);
            });
            this.items.push({
              title: 'Nuclear Medicine',
              filters: {
                list: () => listItems,
              },
              expanded: true,
            });
          }
          if (service.pETScanServices) {
            let listItems: Array<{ item: string; active: boolean }> = [];

            service.pETScanServices.forEach((item) => {
              let itemx: {
                item: string;
                active: boolean;
                cost?: string;
                timespan?: string;
              };

              itemx = {
                item: PETScan[item.service].replace(/_/g, ' '),
                active: this.pETScanServices.some((hf) => hf === item.service)
                  ? true
                  : false,
                cost: item.cost.toFixed(2),
                timespan: `${item.timespan}  ${TimeUnit[item.timeunit]}`,
              };

              listItems.push(itemx);
            });
            this.items.push({
              title: 'PET Scan',
              filters: {
                list: () => listItems,
              },
              expanded: true,
            });
          }
          if (service.ultrasoundServices) {
            let listItems: Array<{ item: string; active: boolean }> = [];

            service.ultrasoundServices.forEach((item) => {
              let itemx: {
                item: string;
                active: boolean;
                cost?: string;
                timespan?: string;
              };

              itemx = {
                item: Ultrasound[item.service].replace(/_/g, ' '),
                active: this.ultrasoundServices.some(
                  (hf) => hf === item.service,
                )
                  ? true
                  : false,
                cost: item.cost.toFixed(2),
                timespan: `${item.timespan}  ${TimeUnit[item.timeunit]}`,
              };

              listItems.push(itemx);
            });
            this.items.push({
              title: 'Ultrasound',
              filters: {
                list: () => listItems,
              },
              expanded: true,
            });
          }
          if (service.xRayServices) {
            let listItems: Array<{ item: string; active: boolean }> = [];

            service.xRayServices.forEach((item) => {
              let itemx: {
                item: string;
                active: boolean;
                cost?: string;
                timespan?: string;
              };

              itemx = {
                item: XRay[item.service].replace(/_/g, ' '),
                active: this.xRayServices.some((hf) => hf === item.service)
                  ? true
                  : false,
                cost: item.cost.toFixed(2),
                timespan: `${item.timespan}  ${TimeUnit[item.timeunit]}`,
              };

              listItems.push(itemx);
            });
            this.items.push({
              title: 'XRay',
              filters: {
                list: () => listItems,
              },
              expanded: true,
            });
          }
          this._cdRef.markForCheck();
        }
      });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
